import axios from "axios";
import { API_ENDPOINTS } from "@/lib/api-endpoints";

type RetryableRequestConfig = {
    _retry?: boolean;
    skipAuthRefresh?: boolean;
    url?: string;
};

export type AuthContext = "BUYER" | "SUPPLIER" | "ADMIN" | "OPERATOR" | "INTERNAL" | "EXTERNAL" | null;

/**
 * SECURITY CONCERN: Module-level mutable state for auth context
 *
 * This variable holds the auth context across requests. Potential issues:
 * 1. Can become stale during rapid route transitions
 * 2. Could leak context between different user sessions in edge cases
 * 3. No automatic cleanup on logout
 *
 * MITIGATION:
 * - Path-based resolution (window.location.pathname) is prioritized as the source of truth
 * - currentAuthContext is only used as a fallback
 * - All requests derive context from current route first
 * - On logout, setAuthContext(null) should be called explicitly
 *
 * FUTURE IMPROVEMENT: Replace with request-scoped context or remove entirely
 * if path-based resolution proves sufficient in all scenarios.
 */
let currentAuthContext: AuthContext = null;

export function setAuthContext(ctx: AuthContext) {
    currentAuthContext = ctx;
}

export function clearAuthContext() {
    currentAuthContext = null;
}

export function resolveAuthContextFromRole(role?: string | null): AuthContext {
    const normalized = (role || "").toUpperCase();
    if (normalized === "BUYER") return "BUYER";
    if (normalized === "SUPPLIER") return "SUPPLIER";
    if (normalized === "ADMIN") return "ADMIN";
    if (normalized === "OPERATOR") return "OPERATOR";
    return null;
}

export function resolveAuthContextFromPath(path?: string | null): AuthContext {
    if (!path) return null;

    const normalized = path.toLowerCase();
    if (normalized.includes("/dashboard/buyer")) return "BUYER";
    if (normalized.includes("/dashboard/supplier")) return "SUPPLIER";
    if (normalized.includes("/dashboard/admin") || normalized.includes("/admin")) return "ADMIN";
    if (normalized.includes("/dashboard/operator")) return "OPERATOR";
    return null;
}

// Single-flight guard for token refresh to avoid parallel refresh races.
let refreshInFlight: Promise<void> | null = null;

const axiosInstance = axios.create({
    baseURL: process.env.NEXT_PUBLIC_API_URL || "/backend-api",
    withCredentials: true, // Include cookies in cross-origin requests (for cookie-based auth)
    headers: {
        "Content-Type": "application/json",
    },
});

/**
 * SECURITY: CSRF Protection Strategy
 *
 * FRONTEND ROLE:
 * - withCredentials: true sends cookies with all requests (allows cookie-based auth)
 * - This is necessary for maintaining authenticated sessions
 *
 * BACKEND SECURITY (Critical):
 * - CSRF protection is currently DISABLED in backend SecurityConfig
 * - To mitigate CSRF without tokens, the backend relies on:
 *   1. SameSite=Strict/Lax cookie attribute (blocks cross-site cookie sending)
 *   2. Origin/Referer header validation by proxy/reverse-proxy
 *   3. CORS policy restrictions
 *
 * PROXY/NGINX RESPONSIBILITY:
 * Since backend CSRF is disabled, the reverse proxy MUST validate:
 * - Origin header matches allowed domains
 * - Referer header comes from same origin (for state-changing requests)
 * - Block requests with mismatched Origin/Referer
 *
 * FUTURE IMPROVEMENT:
 * Implement CSRF token validation in backend instead of relying on proxy.
 * This provides defense-in-depth if proxy is misconfigured or bypassed.
 *
 * TESTING:
 * Verify that cross-origin requests from different domains are rejected.
 * Example: curl -H "Origin: https://attacker.com" [request] should fail
 */

axiosInstance.interceptors.request.use(
    (config) => {
        if (config.data instanceof FormData) {
            if (config.headers && typeof config.headers.delete === 'function') {
                config.headers.delete('Content-Type');
            } else {
                delete config.headers['Content-Type'];
            }
        }

        /**
         * Auth context resolution order (by priority):
         * 1. Path-based context (current URL pathname) - most reliable, least stale
         * 2. Module-level context (setAuthContext) - fallback for edge cases
         *
         * This ensures that even if module state drifts, the active route is the source of truth.
         */
        const pathContext = typeof window !== "undefined"
            ? resolveAuthContextFromPath(window.location.pathname)
            : null;
        const effectiveContext = pathContext ?? currentAuthContext;

        if (effectiveContext) {
            config.headers["X-Auth-Context"] = effectiveContext;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config as RetryableRequestConfig | undefined;
        const shouldSkipRefresh = !!originalRequest?.skipAuthRefresh;
        const shouldRedirectToLogin = () => {
            if (typeof window === "undefined") return false;
            return window.location.pathname.includes("/dashboard");
        };

        const isAuthRefreshCall = originalRequest?.url?.includes(API_ENDPOINTS.REFRESH);
        const isAuthLoginCall = originalRequest?.url?.includes(API_ENDPOINTS.LOGIN);
        const isAuthVerify2FA = originalRequest?.url?.includes(API_ENDPOINTS.VERIFY_2FA);
        const isAuthLogoutCall = originalRequest?.url?.includes(API_ENDPOINTS.LOGOUT);

        const status = error.response?.status;
        const shouldAttemptRefresh = status === 401;

        // Nu incercam refresh pe requesturile de auth initial (evitam bucla infinita)
        // VERIFY_2FA la login nu are token valid încă, deci nu face refresh
        if (shouldAttemptRefresh && (shouldSkipRefresh || isAuthRefreshCall || isAuthLoginCall || isAuthVerify2FA || isAuthLogoutCall)) {
            return Promise.reject(error);
        }

        if (!originalRequest) {
            return Promise.reject(error);
        }

        // Refresh-ul trebuie pornit doar pentru 401 (neautentificat / token expirat).
        // 403 este frecvent un access denied legitim, mai ales pe zonele de admin/operator,
        // și nu trebuie tratat ca logout automat.
        if (shouldAttemptRefresh && !originalRequest._retry) {
            originalRequest._retry = true; // Marchează cererea ca "reîncercată"

            try {
                // 1. Run a single refresh request even if multiple API calls fail together.
                if (!refreshInFlight) {
                    refreshInFlight = axiosInstance
                        .post(API_ENDPOINTS.REFRESH)
                        .then(() => undefined)
                        .finally(() => {
                            refreshInFlight = null;
                        });
                }

                await refreshInFlight;

                // 2. Reîncercăm cererea originală
                // Axios va trimite automat noul cookie 'accessToken' setat de pasul 1
                return axiosInstance(originalRequest);

            } catch (refreshError) {
                // 3. Dacă Refresh-ul dă eroare (cookie expirat sau invalid), delogăm userul
                if (shouldRedirectToLogin()) {
                    // Extragem limba din URL (ex: din "/en/dashboard" extragem "en")
                    const segments = window.location.pathname.split('/');
                    const knownLocales = ['ro', 'en', 'de', 'fr', 'it', 'pl', 'bg', 'hu'];
                    const currentLang = knownLocales.includes(segments[1]) ? segments[1] : 'ro';
                    // Redirecționăm menținând limba - același login pentru toți
                    window.location.href = `/${currentLang}/login`;
                }
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;